import {
  type ApolloError,
  gql,
  useApolloClient,
  useMutation,
} from "@apollo/client";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { SmbAttachmentStatus, type Transaction } from "@/apis/gql/types";
import { useTenant } from "@/providers/session";

import { GET_ALL_TRANSACTIONS } from "./get-all";
import { BANK_TRANSACTION_FRAGMENT } from "./graphql";

type ApproveSmbTransactionAttachmentsInput = {
  bankTransactionIds: string[];
  smbApproved: boolean;
};

export const useApproveSmbTransactionAttachments = (): [
  (input: ApproveSmbTransactionAttachmentsInput) => Promise<Transaction[]>,
  {
    loading: boolean;
    error?: ApolloError;
  },
] => {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const client = useApolloClient();

  const [approveSmbTransactionAttachments, { loading, error }] = useMutation<{
    approveSmbTransactionAttachments: Transaction[];
  }>(APPROVE_SMB_TRANSACTION_ATTACHMENTS_MUTATION, {
    refetchQueries: [GET_ALL_TRANSACTIONS],
    awaitRefetchQueries: true,
    update: (cache, { data }, { variables }) => {
      if (
        !data?.approveSmbTransactionAttachments ||
        !variables?.bankTransactionIds
      ) {
        return;
      }

      variables.bankTransactionIds.forEach((bankTxnId: string) => {
        const existing = cache.readFragment({
          id: `Transaction:${bankTxnId}`,
          fragment: BANK_TRANSACTION_FRAGMENT,
          fragmentName: "BankTransaction",
        });

        if (existing) {
          cache.writeFragment({
            id: `Transaction:${bankTxnId}`,
            fragment: BANK_TRANSACTION_FRAGMENT,
            fragmentName: "BankTransaction",
            data: {
              ...existing,
              ...variables.updatedFields,
            },
          });
        }
      });
    },
    optimisticResponse: ({ bankTransactionIds, smbApproved }) => {
      const optimisticTransactions = bankTransactionIds
        .map((bankTxnId: string) => {
          const cacheId = client.cache.identify({
            __typename: "Transaction",
            bankTxnId: bankTxnId,
          });

          const existingTransaction = client.cache.readFragment({
            id: cacheId,
            fragment: BANK_TRANSACTION_FRAGMENT,
            fragmentName: "BankTransaction",
          });

          if (!existingTransaction) {
            return null;
          }

          return {
            __typename: "Transaction" as const,
            ...existingTransaction,
            smbAttachmentStatus:
              smbApproved === true ? SmbAttachmentStatus.ATTACHED : undefined,
          };
        })
        .filter(Boolean);

      return {
        approveSmbTransactionAttachments: optimisticTransactions,
      };
    },
  });

  const handleApproveSmbTransactionAttachments = async (
    input: ApproveSmbTransactionAttachmentsInput,
  ) => {
    if (!tenantId) {
      throw new Error("Tenant ID is required");
    }

    const toastId = toast.loading(t("generic_loading"));

    try {
      const { data: response } = await approveSmbTransactionAttachments({
        variables: {
          bankTransactionIds: input.bankTransactionIds,
          smbApproved: input.smbApproved,
        },
      });

      if (!response) {
        throw new Error("No response from approveSmbTransactionAttachments");
      }

      toast.dismiss(toastId);

      return response.approveSmbTransactionAttachments;
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          module: "transactions",
          action: "update",
        },
      });
      toast.error(t("generic_error"), { id: toastId, richColors: true });
      throw error;
    }
  };

  return [handleApproveSmbTransactionAttachments, { loading, error }];
};

export const APPROVE_SMB_TRANSACTION_ATTACHMENTS_MUTATION = gql`
  mutation ApproveSmbTransactionAttachments(
    $bankTransactionIds: [String!]!
    $smbApproved: Boolean!
  ) {
    approveSmbTransactionAttachments(
      bankTransactionIds: $bankTransactionIds
      smbApproved: $smbApproved
    ) {
      bankTxnId
      tenantId
      status
      amount
      currency
      createdAt
      counterpartyName
      counterpartyIban
      purpose
      executionDate
      side
      bookingDate
      valueDate
      smbAttachmentStatus
      smbConversationStatus
      invoices {
        invoiceId
        invoiceNo
        totalAmount
        currency
        supplierName
        invoiceDate
      }
    }
  }
`;
